import React from "react";
import { useTheme } from "@material-ui/core/styles";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  CheckCircleOutline,
  ErrorOutline,
  WarningRounded,
  MyLocation,
} from "@material-ui/icons";
import { i18n } from "../../../translate/i18n";
import { SEVERITY } from "./flowLinter";

// Panel de resultados del validador estático (0383 FS-0.8). Presentación pura:
// recibe los hallazgos ya calculados y solo los traduce y los hace navegables.

const translate = (finding) =>
  i18n.t(`flowbuilders.validation.codes.${finding.code}`, {
    nodeLabel: finding.nodeLabel,
    ...finding.params,
  });

const FlowLintPanel = ({ open, onClose, result, onFocusNode }) => {
  const theme = useTheme();
  if (!open || !result) return null;

  const { findings, errors, warnings } = result;
  const isDark = theme.palette.type === "dark";

  return (
    <Paper
      elevation={8}
      style={{
        position: "absolute",
        right: 16,
        bottom: 16,
        width: 380,
        maxWidth: "calc(100% - 32px)",
        maxHeight: "60%",
        display: "flex",
        flexDirection: "column",
        zIndex: 10,
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ padding: theme.spacing(1, 1, 1, 2), gap: 8 }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }} noWrap>
            {i18n.t("flowbuilders.validation.panelTitle") ||
              "Validación del flujo"}
          </Typography>
          {errors.length > 0 && (
            <Chip
              size="small"
              label={errors.length}
              icon={<ErrorOutline style={{ fontSize: 16, color: "#fff" }} />}
              style={{
                backgroundColor: theme.palette.error.main,
                color: "#fff",
                fontWeight: 600,
              }}
            />
          )}
          {warnings.length > 0 && (
            <Chip
              size="small"
              label={warnings.length}
              icon={<WarningRounded style={{ fontSize: 16, color: "#fff" }} />}
              style={{
                backgroundColor: theme.palette.warning.main,
                color: "#fff",
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {findings.length === 0 ? (
        <Box
          display="flex"
          alignItems="center"
          style={{ gap: 8, padding: theme.spacing(2) }}
        >
          <CheckCircleOutline style={{ color: theme.palette.success.main }} />
          <Typography variant="body2">
            {i18n.t("flowbuilders.validation.allGood") ||
              "El flujo no presenta problemas"}
          </Typography>
        </Box>
      ) : (
        <List dense style={{ overflowY: "auto", padding: 0 }}>
          {findings.map((finding, index) => {
            const isError = finding.severity === SEVERITY.ERROR;
            const color = isError
              ? theme.palette.error.main
              : theme.palette.warning.main;
            return (
              <ListItem
                key={`${finding.code}-${finding.nodeId || "flow"}-${index}`}
                button={Boolean(finding.nodeId)}
                onClick={
                  finding.nodeId ? () => onFocusNode(finding.nodeId) : undefined
                }
                style={{
                  borderLeft: `3px solid ${color}`,
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.015)",
                  marginBottom: 1,
                }}
              >
                <ListItemIcon style={{ minWidth: 34 }}>
                  {isError ? (
                    <ErrorOutline style={{ color, fontSize: 20 }} />
                  ) : (
                    <WarningRounded style={{ color, fontSize: 20 }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={translate(finding)}
                  primaryTypographyProps={{ variant: "body2" }}
                />
                {finding.nodeId && (
                  <Tooltip
                    title={
                      i18n.t("flowbuilders.validation.goToNode") || "Ir al nodo"
                    }
                  >
                    <MyLocation
                      style={{
                        fontSize: 16,
                        color: theme.palette.text.secondary,
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                )}
              </ListItem>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default FlowLintPanel;
